import {onlineText} from './online-language.js';
// Vertrag: OnlineStorefrontRequest.kt:61-96, OnlineStorefront.kt:194-225, 269-288; Stand 07.10.2026.
// Ganzzahlige Antwortwerte werden vor jeder Zahlenkonvertierung als BigInt gelesen.
export function parseWire(text) {
  let i = 0;
  const ws = () => { while (i < text.length && /\s/.test(text[i])) i++; };
  function value() {
    ws(); const c = text[i];
    if (c === '"') {
      const start = i++;
      while (i < text.length) {
        if (text[i++] === '"') return JSON.parse(text.slice(start, i));
        if (text[i - 1] === '\\') i++;
      }
    } else if (c === '[' || c === '{') {
      const array = c === '[', out = array ? [] : Object.create(null), end = array ? ']' : '}';
      i++; ws(); if (text[i] === end) { i++; return out; }
      for (;;) {
        ws(); let key;
        if (!array) { if (text[i] !== '"') break; key = value(); ws(); if (text[i++] !== ':') break; }
        const item = value(); if (array) out.push(item); else out[key] = item;
        ws(); if (text[i] === end) { i++; return out; } if (text[i++] !== ',') break;
      }
    } else {
      const token = /^(?:true|false|null|-?(?:0|[1-9]\d*))/.exec(text.slice(i))?.[0];
      if (token) { i += token.length; return /^-?\d+$/.test(token) ? BigInt(token) : JSON.parse(token); }
    }
    throw Error('Ungültige Antwort');
  }
  const result = value(); ws(); if (i !== text.length) throw Error('Ungültige Antwort'); return result;
}
export function cents(value) {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string' && /^-?\d+$/.test(value)) return BigInt(value);
  if (Number.isSafeInteger(value)) return BigInt(value);
  throw Error('Ungültiger Centbetrag');
}
export function centsText(value) {
  const n = cents(value), a = n < 0n ? -n : n;
  return `${n < 0n ? '−' : ''}${a / 100n},${String(a % 100n).padStart(2, '0')} €`;
}
// Namen: backoffice/source/12-reservierungen.jsx:72; Schlüssel: CatalogModel.kt:57-61.
const allergens = {gl:'Gluten',kr:'Krebstiere',ei:'Eier',fi:'Fisch',er:'Erdnüsse',so:'Soja',mi:'Milch',sc:'Schalenfrüchte',se:'Sellerie',sf:'Senf',ses:'Sesam',su:'Sulfite',lu:'Lupinen',we:'Weichtiere'};
// STARTWERT (fragen-nutzer-sammlung-20261005.md, F-23, Nutzerentscheidung offen): Artikel ohne erfasste Allergene bleiben bestellbar mit Hinweis; E-67 verlangt Pflege vor dem Online-Start.
export function allergenText(keys,lang='de') {
  const text = value=>onlineText(value,lang);
  return keys == null ? text('Allergene nicht erfasst – bitte im Betrieb nachfragen') :
    keys.length === 0 ? text('Keine kennzeichnungspflichtigen Allergene') : text('Allergene')+': '+keys.map(k=>text(allergens[k] || 'Nicht zugeordnet – bitte im Betrieb nachfragen')).join(', ');
}

export class OnlineError extends Error {
  constructor(status, problem = {}, retryAfter = null) {
    super(problem.detail || 'Die Anfrage konnte nicht abgeschlossen werden.');
    this.status = status; this.problem = problem;
    // STARTWERT: 2 Sekunden ohne Retry-After; eine Providerfrist hat Vorrang.
    this.retryAt = retryAfter && /^\d+$/.test(retryAfter) ? Date.now() + Number(retryAfter) * 1000 :
      (Date.parse(retryAfter) || Date.now() + 2000);
  }
}
export async function request(path, {signal, ...options} = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener('abort', abort, {once:true});
  // STARTWERT: 15 Sekunden einschließlich Antwortkörper; POST-Ausgang bleibt bei Timeout unklar.
  const timer = setTimeout(abort, 15000);
  try {
    const response = await fetch('/api/v1/online' + path, {credentials:'omit', cache:'no-store', referrerPolicy:'no-referrer', ...options, signal:controller.signal});
    let body;
    try { body = parseWire(await response.text()); }
    catch (e) {
      if (controller.signal.aborted) throw e;
      throw new OnlineError(response.ok ? 0 : response.status, {reason:'invalidResponse'}, response.headers.get('Retry-After'));
    }
    if (!response.ok) throw new OnlineError(response.status, body, response.headers.get('Retry-After'));
    return body;
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}
// Paket N83 (Nachzug N71): 422 ERR-31-01 `variantRequired`/`variantNotAllowed` am Pointer /lines/{i}/variantId
// (zalso_backend/app/src/main/kotlin/zalso/payment/online/OnlineStorefront.kt:149-158, gleicher Pfad fuer quote und
// Bestellung). STARTWERT: Wortlaut mit "Variante" wie das Auswahlfeld (online-storefront.jsx:254) und variantUnavailable,
// statt "Größe" der Kasse (LiveVariants.kt:46-47) - Leitplanke DEC-138, nur Text.
export function problemText(error,lang='de') {
  const p = error.problem || {};
  if (p.reason==='belowMinimumOrder') return onlineText('Der Mindestbestellwert ist nicht erreicht.',lang)+
    (p.missingCents!=null?' '+onlineText('Es fehlen',lang)+' '+centsText(p.missingCents)+'.':'');
  return onlineText(({modeDisabled:'Diese Bestellart ist nicht verfügbar.', postalCodeMissing:'Bitte die Postleitzahl eingeben.',
    postalCodeOutsideArea:'Diese Postleitzahl liegt außerhalb des Liefergebiets.',
    orderingClosed:'Der Shop nimmt gerade keine Bestellungen an.', orderingUnavailable:'Der Shop nimmt gerade keine Bestellungen an.', requestedForInvalid:'Bitte eine gültige Wunschzeit wählen.',
    phoneRequired:'Für die Lieferung ist eine Telefonnummer erforderlich.', paymentUnavailable:'Zahlung derzeit nicht verfügbar. Bitte dieselbe Bestellung erneut versuchen.',
    articleUnavailable:'Ein Artikel ist nicht mehr verfügbar. Bitte den Warenkorb prüfen.', variantUnavailable:'Eine Variante ist nicht mehr verfügbar.',
    variantRequired:'Bitte für diesen Artikel eine Variante wählen.', variantNotAllowed:'Dieser Artikel hat keine Varianten mehr. Bitte den Artikel neu wählen.',
    extraUnavailable:'Ein Extra ist nicht mehr verfügbar.', inProgress:'Die Bestellung wird noch bearbeitet. Bitte denselben Versuch erneut fortsetzen.',
    invalidResponse:'Die Bestellantwort ist unvollständig. Bitte denselben Versuch fortsetzen.',
    secureContextRequired:'Bestellen benötigt eine sichere HTTPS-Verbindung und einen aktuellen Browser.',
    paymentConfigurationMissing:'Zahlung ist noch nicht eingerichtet. Es wurde keine neue Bestellung gesendet.',
    shopUnknown:'Shop nicht gefunden.',
    browserStorageUnavailable:'Der Sitzungsspeicher ist nicht verfügbar. Bitte den Speicherzugriff erlauben und denselben Versuch fortsetzen.',
    idempotencyKeyReused:'Diese Bestellung kann nicht verändert wiederholt werden. Bitte den ursprünglichen Versuch prüfen.'})[p.reason] ||
    ([429,503].includes(error.status) ? 'Vorübergehend nicht verfügbar. Bitte dieselbe Bestellung später erneut versuchen.' :
      error.status === 404 ? 'Shop oder Bestellung nicht gefunden.' :
      error.status ? 'Bitte die Angaben prüfen oder die ursprüngliche Bestellung erneut versuchen.' : 'Die Antwort ist ausgeblieben. Bitte dieselbe Bestellung erneut versuchen.'),lang);
}
export function fieldErrors(error,lang='de') {
  const rules = {required:'Bitte ausfüllen.',email:'Bitte eine gültige E-Mail-Adresse eingeben.',
    textFormat:'Bitte einzeiligen Text ohne Steuerzeichen innerhalb der erlaubten Länge eingeben.',range:'Bitte eine Menge von 1 bis 99 wählen.'};
  return Object.fromEntries((error.problem?.errors || []).map(e => [e.pointer, rules[e.rule] ? onlineText(rules[e.rule],lang) : problemText(error,lang)]));
}
const lineBody = l => ({articleId:l.articleId, ...(l.variantId ? {variantId:l.variantId} : {}), extraIds:l.extraIds, quantity:l.quantity});
export function orderBody(mode, postalCode, lines, guest, address, requestedFor) {
  const body = {mode, guest:Object.fromEntries(Object.entries(guest).map(([k,v]) => [k,v.trim()]).filter(([,v]) => v)),
    lines:lines.map(l => ({...lineBody(l), ...(cleanNote(l.note) ? {note:cleanNote(l.note)} : {})}))};
  if (mode === 'DELIVERY') { body.postalCode = postalCode.trim(); body.address = {street:address.street.trim(), city:address.city.trim(), ...(address.note?.trim() ? {note:cleanNote(address.note)} : {})}; }
  if (requestedFor) body.requestedFor = requestedFor;
  return body;
}
// ARBEITSUEBERGABE-20261016 §7, OnlineStorefront.quote: unverbindliche Preisauskunft desselben Preispfads wie die
// Bestellanlage; Hinweise beeinflussen keinen Preis und werden nicht gesendet. Nichts wird gespeichert oder reserviert.
export const quoteBody = lines => JSON.stringify({lines:lines.map(lineBody)});
export async function postQuote(slug, body, signal) {
  const quote = await request(`/shops/${encodeURIComponent(slug)}/quote`, {method:'POST', signal,
    headers:{'Content-Type':'application/json'}, body});
  if (!Array.isArray(quote.lines) || quote.lines.length !== JSON.parse(body).lines.length || typeof quote.subtotalCents !== 'bigint' ||
    quote.lines.some(l => typeof l.lineTotalCents !== 'bigint')) throw new OnlineError(0, {reason:'invalidResponse'});
  return {...quote, receivedAt:Date.now()};
}
export function quoteProblemText(error, lang='de') {
  const p = error.problem || {}, line = /^\/lines\/(\d+)\//.exec(p.errors?.[0]?.pointer || '');
  const prefix = line ? `${onlineText('Position',lang)} ${Number(line[1])+1}: ` : '';
  return prefix + (['articleUnavailable','variantUnavailable','variantRequired','variantNotAllowed','extraUnavailable'].includes(p.reason) ? problemText(error,lang) :
    onlineText(p.reason === 'amountOutOfRange' ? 'Der Warenkorb übersteigt den zulässigen Betrag.' :
      'Preise derzeit nicht verfügbar. Bitte später erneut versuchen.', lang));
}
// OnlineStorefront.articleMenu/quote: Regelname und Ende des heutigen Fensters in der Betriebszeitzone.
export function happyHourText(happyHour, timeZone, lang='de') {
  return happyHour ? `${happyHour.label} ${onlineText('bis',lang)} ${localTime(new Date(happyHour.until), timeZone).time}` : '';
}
// Gerätezeitpunkt eines Aktionsendes, gemessen ab Empfang und Serverzeit der Antwort (Geräteuhr ohne Einfluss).
export const changeAt = (happyHour, answer) => answer.receivedAt + Date.parse(happyHour.until) - Date.parse(answer.pricedAt);
export const attemptKey = slug => 'zalso-online-attempt:' + slug;
export function loadAttempt(slug) {
  try { return JSON.parse(sessionStorage.getItem(attemptKey(slug))) || null; } catch { return null; }
}
export function saveAttempt(slug, attempt) {
  // Vor dem ersten POST muss der unveränderliche Versuch gespeichert sein; Fehler verhindert den POST.
  try { sessionStorage.setItem(attemptKey(slug), JSON.stringify(attempt)); }
  catch { throw new OnlineError(0,{reason:'browserStorageUnavailable'}); }
}
export function newAttempt(slug, body) {
  const pending = loadAttempt(slug);
  if (pending) return pending;
  if (typeof globalThis.crypto?.randomUUID !== 'function') throw new OnlineError(0,{reason:'secureContextRequired'});
  const attempt = {orderId:crypto.randomUUID(), body:JSON.stringify(body)};
  saveAttempt(slug, attempt); return attempt;
}
export function postAttempt(slug, attempt, signal) {
  return request(`/shops/${encodeURIComponent(slug)}/orders`, {method:'POST', signal,
    headers:{'Content-Type':'application/json', 'Idempotency-Key':attempt.orderId}, body:attempt.body});
}
export function statusLocation(slug, orderId) {
  return `/${encodeURIComponent(slug)}/bestellung/${encodeURIComponent(orderId)}`;
}
export function routeInfo(path = location.pathname, hash = location.hash) {
  const match = /^\/([^/]+)\/bestellung\/([0-9a-f-]{36})\/?$/.exec(path);
  const token = new URLSearchParams(hash.slice(1)).get('t') || (match && loadStatusToken(match[2]));
  return match ? {slug:match[1], orderId:match[2], token} : null;
}
// ShopOrderingWindow.kt:35-39: tägliches, halboffenes Zeitfenster in der Betriebszeitzone; from === to ist ungültig,
// from > to läuft über Mitternacht (Paket W11a, Nutzerentscheidung 20.10.2026).
const formatters = new Map();
export function localTime(instant, timeZone) {
  if (!formatters.has(timeZone)) formatters.set(timeZone, new Intl.DateTimeFormat('sv-SE', {timeZone, year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', hourCycle:'h23'}));
  const parts = formatters.get(timeZone).formatToParts(instant);
  const p = Object.fromEntries(parts.map(x => [x.type,x.value]));
  return {day:`${p.year}-${p.month}-${p.day}`, time:`${p.hour}:${p.minute}`};
}
// ShopOrderingWindow.kt:36-38: Fenster nicht leer, Ortszeit "HH:MM" im Fenster; die Modusprüfung (:36) bleibt beim Aufrufer.
export function acceptsAt(a, time) {
  if (!a || a.from === a.to) return false;
  return a.from < a.to ? time >= a.from && time < a.to : time >= a.from || time < a.to;
}
// ShopOrderingWindow.kt:41-50: Öffnungstag ist der Vortag für den Teil nach Mitternacht eines Fensters über Mitternacht.
export function openingDay(shop, instant) {
  const a = shop.acceptance, p = localTime(instant, a.timeZone);
  if (!(a.from > a.to && p.time < a.to)) return p.day;
  const d = new Date(p.day + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
export function slots(shop, now = Date.now()) {
  const a = shop.acceptance, result = [];
  if (!shop.preorderEnabled || !a || a.from === a.to) return result;
  const zoneFormat = new Intl.DateTimeFormat('de-DE',{timeZone:a.timeZone,timeZoneName:'shortOffset'});
  // STARTWERT: 15-Minuten-Raster, wie bestehende Bezahlseite; maximal sieben Tage laut Backend.
  // Aufsteigend erzeugt; Slots nach Mitternacht tragen den Öffnungstag (OnlineStorefront.kt:216-224): 23:30, 00:00, 00:30.
  const start = Math.ceil((now + 1) / 900000) * 900000;
  for (let at = start; at <= now + 7 * 86400000; at += 900000) {
    const p = localTime(new Date(at), a.timeZone);
    if (Number(p.time.slice(3)) % 15 === 0 && acceptsAt(a, p.time)) {
      const offset = zoneFormat.formatToParts(new Date(at)).find(p => p.type === 'timeZoneName').value;
      result.push({value:new Date(at).toISOString(), label:`${p.day} · ${p.time} (${offset})`, day:openingDay(shop, new Date(at)), time:p.time, offset});
    }
  }
  return result;
}
// Die öffentliche Shopansicht liefert kein opensAt (OnlineShopJson.kt:117-124; nur das Problem orderingClosed trägt es,
// OnlineStorefront.kt:447-449). Daher wie ShopOrderingWindow.kt:58-77 der erste Zeitpunkt ab jetzt, an dem das Fenster
// offen ist, minutengenau und einschließlich Zeitumstellung; Horizont zwei Tage wie bisher.
export function nextOpening(shop, now = Date.now()) {
  const a = shop.acceptance;
  if (!a || a.from === a.to || (!shop.pickup.enabled && !shop.delivery.enabled)) return null;
  for (let at = Math.ceil(now / 60000) * 60000; at < now + 2 * 86400000; at += 60000) {
    const p = localTime(new Date(at), a.timeZone);
    if (acceptsAt(a, p.time)) return `${p.day} · ${p.time} (${a.timeZone})`;
  }
  return null;
}
// HttpJson.kt:184-190: nur U+0020 als Leerzeichen; keine Kontroll-/Formatzeichen.
export const cleanNote = value => (value || '').replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\p{Zs}\p{Cs}]/gu, ' ').trim();
export const validSlug = value => /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/.test(value);
export function shopSlug(path, configured = '') {
  // Build-Slug nur für den Vorschau-Einstieg /storefront/; /storefront ist ein öffentlicher Slug.
  return path === '/storefront/' ? configured : path.split('/').filter(Boolean)[0] || '';
}
export const terminalStatus = status => ['accepted','rejected','expired','paymentFailed','cancelled'].includes(status);
export const canRestart = error => error?.status === 404 || error?.problem?.reason === 'idempotencyKeyReused' || error?.problem?.outcomeKnown === true;
export const pollDelay = error => Math.max(10000, (error?.retryAt || 0) - Date.now());
export const statusTokenKey = id => 'zalso-online-status:' + id;
export function saveStatusToken(id, token) {
  try { sessionStorage.setItem(statusTokenKey(id), token); }
  catch { throw new OnlineError(0,{reason:'browserStorageUnavailable'}); }
}
export function loadStatusToken(id) {
  try { return sessionStorage.getItem(statusTokenKey(id)); } catch { return null; }
}
export function discardAttempt(slug) {
  try { sessionStorage.removeItem(attemptKey(slug)); }
  catch { throw new OnlineError(0,{reason:'browserStorageUnavailable'}); }
}
